<template>
  <Navbar :center-text="$t('DiagnosticsSearchView.agroecologicalDiagnostics')"
          :message="this.message"
          :message-type="this.messageType">
  </Navbar>
  <form class="container mt-5 mb-5 w-100">
    <!-- Farmer / ID -->
    <div class="row mb-4 justify-content-center">
      <div class="col-md-6 col-sm-12 form-group">
        <label for="farmer"
               class="form-label">
          {{ $t('farmerID') }}
        </label>

        <multiselect placeholder=""
                     v-model="selectedFarms"
                     :options="availableFarms"
                     :custom-label="farmSelectLabel"
                     :multiple="true"
                     :searchable="true"
                     :close-on-select="true"
                     :clear-on-select="true"
                     :show-labels="false"
                     :preselect-first="true"
                     track-by="__id"
                     required></multiselect>
      </div>
    </div>

    <div class="row mb-4 justify-content-center">
      <div class="col-md-6 col-sm-12 form-group">
        <label for="category"
               class="form-label">
          {{ $t('DiagnosticsSearchView.showIndicatorsOf') }}
        </label>
        <select v-model="chosenCategory"
                class="form-control">
          <option value="dimensions" selected>{{ $t('DiagnosticsSearchView.dimensions') }}</option>
          <option value="principles">{{ $t('DiagnosticsSearchView.agroecoPrinciples') }}</option>
        </select>
      </div>
    </div>

    <div class="row mb-4 justify-content-center"
         v-if="this.chosenCategory === 'dimensions'">
      <div class="col-md-6 col-sm-12 form-group">
        <label for="dimensions"
               class="form-label">
          {{ $t('DiagnosticsSearchView.dimensions') }}
        </label>
        <multiselect placeholder=""
                     class="rounded-pill"
                     v-model="selectedDimensions"
                     :options="availableDimensions"
                     :custom-label="dimensionSelectLabel"
                     :multiple="true"
                     :searchable="true"
                     :close-on-select="true"
                     :clear-on-select="true"
                     :show-label="false"
                     track-by="id"
                     required></multiselect>
      </div>
    </div>

    <div class="row mb-4 justify-content-center"
         v-if="this.chosenCategory === 'principles'">
      <div class="col-md-6 col-sm-12 form-group">
        <label for="dimensions"
               class="form-label">
          {{ $t('DiagnosticsSearchView.agroecoPrinciples') }}
        </label>
        <multiselect placeholder=""
                     class="rounded-pill"
                     v-model="selectedPrinciples"
                     :options="availablePrinciples"
                     :custom-label="principleSelectLabel"
                     :multiple="true"
                     :searchable="true"
                     :close-on-select="true"
                     :clear-on-select="true"
                     :show-label="false"
                     track-by="id"
                     required></multiselect>
      </div>
    </div>

    <!-- Timespan -->
    <div class="row mb-4 justify-content-center">
      <div class="col-md-3 col-sm-12">
        <label for="start-date"
               class="form-label">
          {{ $t('startDate') }}
        </label>
        <input
            id="start-date"
            type="date"
            class="form-control rounded-pill"
            v-model="startDate"
        />
      </div>
      <div class="col-md-3 col-sm-12">
        <label for="end-date"
               class="form-label">
          {{ $t('endDate') }}
        </label>
        <input
            id="end-date"
            type="date"
            class="form-control rounded-pill"
            v-model="endDate"/>
      </div>
    </div>

    <div class="row mb-4 justify-content-center">
      <div class="col-md-3 col-sm-12 form-group">
        <label for="comparOverTime"
               class="form-label">
          {{ $t('DiagnosticsSearchView.show') }}
        </label>
        <select v-model="compareOverTime"
                class="form-control"
                id="comparOverTime">
          <option value=false>{{ $t('DiagnosticsSearchView.onlyMostRecent') }}</option>
          <option value=true selected>{{ $t('DiagnosticsSearchView.compareOverTime') }}</option>
        </select>
      </div>

      <div class="col-md-3 col-sm-12 form-group">
        <div class="form-check form-switch">
          <input type="checkbox"
                 name="showAverage"
                 id="showAverage"
                 class="form-check-input mx-1"
                 v-model="showAverage"
                 disabled/>
          <label class="form-label"
                 for="showAverage">
            {{ $t('DiagnosticsSearchView.showAverage') }}
          </label>
        </div>
      </div>
    </div>

  </form>

  <div
      v-if="(selectedDimensions.length > 0 || selectedPrinciples.length > 0) && selectedFarms.length > 0 && isValidTimespan"
      class="container border-top w-100">

    <DiagnosticsTotalsOverviewComponent
        v-if="chosenCategory === 'dimensions' && selectedDimensions.length > 0 && selectedFarms.length > 0 && isValidTimespan"
        :selected-dimensions="selectedDimensions"
        :farms="selectedFarms"
        :show-average="showAverage"
        :compare-over-time="compareOverTime"
        :start-date="startDate"
        :end-date="endDate">
    </DiagnosticsTotalsOverviewComponent>

    <FileExport
        v-if="((selectedDimensions.length > 0 && chosenCategory === 'dimensions') || (selectedPrinciples.length > 0 && chosenCategory === 'principles')) && selectedFarms.length > 0 && isValidTimespan"
        :filename="this.exportFilename"
        :data="this.exportData">
    </FileExport>

    <!-- Show results for dimensions -->
    <DiagnosticsCategoryComponent
        v-if="selectedDimensions.length > 0 && chosenCategory === 'dimensions' && selectedFarms.length > 0 && isValidTimespan"
        v-for="dimension in selectedDimensions"
        :key="dimension.id"
        :category="dimension"
        :type="chosenCategory"
        :show-average="showAverage"
        :farms="selectedFarms"
        :start-date="startDate"
        :end-date="endDate"
        :compare-over-time="compareOverTime"></DiagnosticsCategoryComponent>

    <!-- Show results for principles -->
    <DiagnosticsCategoryComponent
        v-if="selectedPrinciples.length > 0 && chosenCategory === 'principles' && selectedFarms.length > 0  && isValidTimespan"
        v-for="principle in selectedPrinciples"
        :key="principle.id"
        :category="principle"
        :type="chosenCategory"
        :show-average="showAverage"
        :farms="selectedFarms"
        :start-date="startDate"
        :end-date="endDate"
        :compare-over-time="compareOverTime"></DiagnosticsCategoryComponent>
  </div>

  <Footerbar></Footerbar>
</template>

<script>
import Navbar from "@/components/Navbar.vue";
import {sharedState} from "@/store";
import {
  getAllDiagnosticsDimensions,
  getAllDiagnosticsFarms,
  getAllDiagnosticsPrinciples,
  getDiagnosticsResults,
  getDimensionIndicators,
  getPrincipleIndicators,
  flattenForSheet
} from "@/utils";
import Footerbar from "@/components/Footerbar.vue";
import Multiselect from "vue-multiselect";
import FileExport from "@/components/FileExport.vue";
import DiagnosticsCategoryComponent from "@/views/diagnostics/DiagnosticsCategoryComponent.vue";
import DiagnosticsTotalsOverviewComponent from "@/views/diagnostics/DiagnosticsTotalsOverviewComponent.vue";

export default {
  data() {
    return {
      message: "",
      messageType: "",
      isValidTimespan: true,
      startDate: "",
      endDate: "",
      sharedState,
      categoryRefs: [],
      availableCategories: ["dimensions", "principles"],
      chosenCategory: "dimensions",
      availableFarms: [],
      selectedFarms: [],
      availableDimensions: [],
      selectedDimensions: [],
      selectedPrinciples: [],
      availablePrinciples: [],
      showAverage: false,
      compareOverTime: false,
      exportFilename: "",
      exportData: {},
    };
  },
  components: {
    DiagnosticsTotalsOverviewComponent,
    FileExport,
    DiagnosticsCategoryComponent,
    Multiselect,
    Footerbar,
    Navbar,
  },
  async mounted() {
    this.endDate = new Date().toISOString().slice(0, 10);
    this.startDate = new Date();
    this.startDate.setFullYear(this.startDate.getFullYear() - 1);
    this.startDate = this.startDate.toISOString().slice(0, 10);

    let dimensionsResponse = await getAllDiagnosticsDimensions();
    if (dimensionsResponse.status === 200) {
      this.availableDimensions = dimensionsResponse.data;
      this.selectedDimensions = dimensionsResponse.data;
    } else {
      this.message = dimensionsResponse.response.data.message;
      this.messageType = "danger";
    }

    let principlesResponse = await getAllDiagnosticsPrinciples();
    if (principlesResponse.status === 200) {
      this.availablePrinciples = principlesResponse.data;
      this.selectedPrinciples = principlesResponse.data;
    } else {
      this.message = principlesResponse.response.data.message;
      this.messageType = "danger";
    }

    let farmResponse = await getAllDiagnosticsFarms();
    if (farmResponse.status === 200) {
      this.availableFarms = farmResponse.data;
    } else if (farmResponse.status === 204) {
      this.availableFarms = [];
    } else {
      this.message = farmResponse.response.data.message;
      this.messageType = "danger";
    }
  },
  methods: {
    farmSelectLabel({label, id}) {
      return `${label}`;
    },
    dimensionSelectLabel({id, name}) {
      return `${name}`;
    },
    principleSelectLabel({id, name}) {
      return `${name}`;
    },
    async prepareFileExport() {
      if (((this.selectedDimensions.length > 0 && this.chosenCategory === 'dimensions') ||
              (this.selectedPrinciples.length > 0 && this.chosenCategory === 'principles'))
          && this.selectedFarms.length > 0
          && this.isValidTimespan) {

        const date = new Date().toISOString().slice(0, 10);
        this.exportFilename = date + "-CANALLS-Diagnostics-" + this.chosenCategory;
        this.exportData = {};

        if (this.chosenCategory === "dimensions") {
          let data = this.selectedDimensions;
          for (const dimension of data) {
            const index = data.indexOf(dimension);
            const indicatorResponse = await getDimensionIndicators(dimension.id);

            let farmIds = this.selectedFarms.map((farm) => farm.__id);
            let indicatorIds = indicatorResponse.data.map((indicator) => indicator.id);
            let resultResponse = await getDiagnosticsResults(farmIds, indicatorIds, this.showAverage, this.startDate, this.endDate);
            let results = {};
            if (resultResponse.status === 200) {
              results = resultResponse.data;
            }

            let indicators = indicatorResponse.data.map(({
                                                           id,
                                                           name,
                                                           description,
                                                           weight_in_dimension,
                                                           minimum_value,
                                                           maximum_value
                                                         }) => ({
              id,
              name,
              description,
              weight_in_dimension,
              minimum_value,
              maximum_value
            }));
            data[index]['indicators'] = indicators;
            data[index]['indicators'].forEach((indicator) => {
              indicator['farms'] = this.selectedFarms.map(({__id, label}) => ({__id, label}));
              indicator['farms'].forEach((farm) => {
                let date = Object.keys(results[farm.__id])[0];
                farm['geopoint_latitude'] = results[farm.__id][date]['geopoint_latitude'];
                farm['geopoint_longitude'] = results[farm.__id][date]['geopoint_longitude'];
                farm['total_land'] = results[farm.__id][date]['total_land_use'];
                farm['date_diagnostic'] = date;
                farm['indicator_value'] = results[farm.__id][date]['values'][indicator.id]['indicator_value'];
                farm['normalized_indicator_value'] = (results[farm.__id][date]['values'][indicator.id]['indicator_value'] / indicator['maximum_value']) * 100;
              });
            });
          }

          data = data.map(({label, name, indicators}) => ({label, name, indicators}));
          this.exportData = this.flattenForSheet(data);

        } else if (this.chosenCategory === "principles") {
          let data = this.selectedPrinciples;
          for (const principle of data) {
            const index = data.indexOf(principle);
            const indicatorResponse = await getPrincipleIndicators(principle.id);

            let farmIds = this.selectedFarms.map((farm) => farm.__id);
            let indicatorIds = indicatorResponse.data.map((indicator) => indicator.id);
            let resultResponse = await getDiagnosticsResults(farmIds, indicatorIds, this.showAverage, this.startDate, this.endDate);
            let results = {};
            if (resultResponse.status === 200) {
              results = resultResponse.data;
            }
            ;

            let indicators = indicatorResponse.data.map(({
                                                           id,
                                                           name,
                                                           description,
                                                           weight_in_dimension,
                                                           minimum_value,
                                                           maximum_value
                                                         }) => ({
              id,
              name,
              description,
              weight_in_dimension,
              minimum_value,
              maximum_value
            }));

            data[index]['indicators'] = indicators;
            data[index]['indicators'].forEach((indicator) => {
              indicator['farms'] = this.selectedFarms.map(({__id, label}) => ({__id, label}));
              indicator['farms'].forEach((farm) => {
                let date = Object.keys(results[farm.__id])[0];
                farm['geopoint_latitude'] = results[farm.__id][date]['geopoint_latitude'];
                farm['geopoint_longitude'] = results[farm.__id][date]['geopoint_longitude'];
                farm['total_land'] = results[farm.__id][date]['total_land_use'];
                farm['date_diagnostic'] = date;
                farm['indicator_value'] = results[farm.__id][date]['values'][indicator.id]['indicator_value'];
                farm['normalized_indicator_value'] = (results[farm.__id][date]['values'][indicator.id]['indicator_value'] / indicator['maximum_value']) * 100;
              });
            });
          }

          data = data.map(({label, name, indicators}) => ({label, name, indicators}));
          this.exportData = flattenForSheet(data);
        }
      }
    }
  },
  watch: {
    chosenCategory(newVal, oldVal) {
      this.prepareFileExport();
    },
    selectedFarms(newVal, oldVal) {
      this.prepareFileExport();
    },
    selectedDimensions(newVal, oldVal) {
      this.prepareFileExport();
    },
    selectedPrinciples(newVal, oldVal) {
      this.prepareFileExport();
    },
    startDate(newVal, oldVal) {
      if (Date.parse(newVal) >= Date.parse(this.endDate)) {
        this.message = this.$t('selectValidTimespan');
        this.messageType = "danger";
        this.isValidTimespan = false;
      } else {
        this.message = '';
        this.messageType = '';
        this.isValidTimespan = true;
        this.prepareFileExport();
      }
    },
    endDate(newVal, oldVal) {
      if (Date.parse(newVal) <= Date.parse(this.startDate)) {
        this.message = this.$t('selectValidTimespan');
        this.messageType = "danger";
        this.isValidTimespan = false;
      } else {
        this.message = '';
        this.messageType = '';
        this.isValidTimespan = true;
        this.prepareFileExport();
      }
    }
  }
};
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>